import { Enemy } from "../enemies/Enemy";
import { Engine } from "../Engine";
import { GameConstants } from "../GameConstants";
import { MathUtils } from "../utils/MathUtils";

export class Turret {

    public static readonly DOWNGRADE_PERCENT = .97;

    public id: number;
    public creationTick: number;
    public type: string;
    public level: number;
    public maxLevel: number;
    public x: number;
    public y: number;
    public damage: number;
    public reload: number;
    public range: number;
    public inflicted: number;
    public priceUpgrade: number;
    public value: number;
    public position: {r: number, c: number};
    public shootingStrategy: string;
    public shootingStrategyIndex: number;
    public fixedTarget: boolean;
    public enemiesWithinRange: Enemy[];
    public followedEnemy: Enemy;
    public shootAngle: number;
    public projectileSpeed: number;
    public constructing: boolean;
    public constructionTicks: number;
    public t1: number;
    public t2: number;
    public upgrading: boolean;
    public upgradeTicks: number;

    protected reloadTicks: number;
    protected readyToShoot: boolean;
    protected engine: Engine;
    protected throwsMines: boolean;
    
    constructor (type: string, p: {r: number, c: number}, engine: Engine, skipBuild?: boolean, level?: number) {

        this.engine = engine;

        this.id = engine.turretId;
        engine.turretId ++;

        this.creationTick = this.engine.ticksCounter;
        
        this.type = type;
        this.t1 = 0;
        this.t2 = 0;
        this.level = 1;
        this.maxLevel = 3;
        this.inflicted = 0;
        this.position = p;
        this.fixedTarget = true;
        this.shootingStrategyIndex = 0;
        this.shootingStrategy = GameConstants.STRATEGIES_ARRAY[this.shootingStrategyIndex];
        this.readyToShoot = true;
        this.enemiesWithinRange = [];
        this.followedEnemy = null;
        this.throwsMines = false;
        this.constructing = true;
        this.constructionTicks = 25;
        this.upgrading = false;
        this.upgradeTicks = 25;

        this.x = this.position.c + .5;
        this.y = this.position.r + .5;

        this.value = Math.round(this.engine.turretData[this.type].price);

        if (skipBuild) {
            this.t2 = this.constructionTicks - 1;
        }

        if (level) {
            this.level = level;
        }
    }

    public destroy(): void {
        //
    }

    public update(): void {

        if (this.constructing) {

            this.t2 ++;

            if (this.t2 === this.constructionTicks) {
                this.constructing = false;
                this.engine.onTurretReady(this.id);
            }

            return;
        }

        if (this.upgrading) {

            this.t2 ++;

            if (this.t2 === this.upgradeTicks) {
                this.upgrading = false;
                this.upgrade();
            }
        }

        this.enemiesWithinRange = this.getEnemiesWithinRange();

        if (this.fixedTarget) {
            if (this.enemiesWithinRange.length > 0) {
                if (this.enemiesWithinRange.indexOf(this.followedEnemy) === -1) {
                    this.followedEnemy = this.enemiesWithinRange[0];
                }   
            } else {
                this.followedEnemy = null;
            }
        } else {
            this.followedEnemy = this.enemiesWithinRange[0];
        }

        if (this.readyToShoot) {

            if (this.enemiesWithinRange.length > 0 || this.throwsMines) {
                this.readyToShoot = false;   
                this.shoot();
            }
        
        } else {

            this.t1 ++;

            if (this.t1 >= this.reloadTicks) {
                this.readyToShoot = true;
                this.t1 = 0;
            }
        }
    }

    public setToUpgrade(): void {

        this.upgrading = true;
        this.t2 = 0;                                                                                                  
    }

    public upgrade(): void {

        this.value += Math.round(this.priceUpgrade);

        this.level ++;
        this.calculateTurretParameters();     
        
        this.engine.onTurretUpgraded(this.id);
    }

    public getRangeAfterUpgrade(): number {

        return this.engine.turretData[this.type].attributes[GameConstants.ATTRIBUTE_RANGE][this.level];
    }

    public setNextStrategy(): void {

        this.shootingStrategyIndex = this.shootingStrategyIndex === GameConstants.STRATEGIES_ARRAY.length - 1 ? 0 : this.shootingStrategyIndex + 1;
        this.shootingStrategy = GameConstants.STRATEGIES_ARRAY[this.shootingStrategyIndex];
    }

    public setFixedTarget(): void {

        this.fixedTarget = !this.fixedTarget;
    }

    protected calculateTurretParameters(): void {
        
        this.reloadTicks = Math.floor(GameConstants.RELOAD_BASE_TICKS * this.reload);
    }

    protected shoot(): void {
        // override
    }

    protected getEnemiesWithinRange(): Enemy [] {

        let enemiesAndSquaredDistances: {enemy: Enemy, squareDist: number} [] = [];
        const squaredRange = MathUtils.fixNumber(this.range * this.range);
        
        for (let i = 0; i < this.engine.enemies.length; i ++) {

            const enemy = this.engine.enemies[i];

            if (this.type === GameConstants.TURRET_TELEPORT && enemy.hasBeenTeleported) {
                continue;
            }

            if (this.type === GameConstants.TURRET_SPLAT && enemy.type === GameConstants.ENEMY_WIND) {
                continue;
            }

            if (enemy.life > 0 && !enemy.teleporting) {

                const dx = this.x - enemy.x;
                const dy = this.y - enemy.y;

                const squaredDist = MathUtils.fixNumber(dx * dx + dy * dy);    

                if (squaredRange >= squaredDist) {   
                    enemiesAndSquaredDistances.push({enemy: enemy, squareDist: squaredDist});
                }
            }
        }

        if (enemiesAndSquaredDistances.length > 1 ) {
    
            // ordenar a los enemigos dentro del radio de acción según la estrategia de disparo
            switch (this.shootingStrategy) {

                case GameConstants.STRATEGY_SHOOT_LAST:
                    enemiesAndSquaredDistances = MathUtils.mergeSort(enemiesAndSquaredDistances, (e1, e2) => (e1.enemy.l - e2.enemy.l) < 0);
                    break;

                case GameConstants.STRATEGY_SHOOT_CLOSEST:
                   
                    enemiesAndSquaredDistances = MathUtils.mergeSort(enemiesAndSquaredDistances, function(e1: {enemy: Enemy, squareDist: number} , e2: {enemy: Enemy, squareDist: number}): boolean {
                        
                        let ret: boolean;

                        if (e1.squareDist === e2.squareDist) {
                            ret = (e1.enemy.l - e2.enemy.l) > 0;
                        } else {
                            ret = (e1.squareDist - e2.squareDist) < 0;
                        }

                        return ret;
                    });

                    break;

                case GameConstants.STRATEGY_SHOOT_WEAKEST:
        
                    enemiesAndSquaredDistances = MathUtils.mergeSort(enemiesAndSquaredDistances, function(e1: {enemy: Enemy, squareDist: number} , e2: {enemy: Enemy, squareDist: number}): boolean {
                        
                        let ret: boolean;

                        if (e1.enemy.life === e2.enemy.life) {
                            ret = (e1.enemy.l - e2.enemy.l) > 0;
                        } else {
                            ret = e1.enemy.life - e2.enemy.life < 0;
                        }

                        return ret;
                    });
                    
                    break;

                case GameConstants.STRATEGY_SHOOT_STRONGEST:

                    enemiesAndSquaredDistances = MathUtils.mergeSort(enemiesAndSquaredDistances, function(e1: {enemy: Enemy, squareDist: number} , e2: {enemy: Enemy, squareDist: number}): boolean {
                        
                        let ret: boolean;

                        if (e1.enemy.life === e2.enemy.life) {
                            ret = (e1.enemy.l - e2.enemy.l) > 0;
                        } else {
                            ret = e1.enemy.life - e2.enemy.life > 0;
                        }

                        return ret;
                    });
                    
                    break;

                case GameConstants.STRATEGY_SHOOT_FIRST:

                    enemiesAndSquaredDistances = MathUtils.mergeSort(enemiesAndSquaredDistances, (e1, e2) => (e1.enemy.l - e2.enemy.l) > 0);
                    break;

                default:
            }
        }

        const enemies: Enemy[] = [];

        for (let i = 0; i < enemiesAndSquaredDistances.length; i ++) {
            enemies.push(enemiesAndSquaredDistances[i].enemy);
        }

        return enemies;
    }
}
